<template>
    <div
        class="comment"
        :class="{ '-removed': isRemoved }"
    >
        <div class="comment__inner">
            <template v-if="!isEditing">
                <div class="comment__avatar">
                    <div class="comment__avatarImage">
                        <avatar-image
                            v-if="isCurrentUserComment && !isRemoved"
                            :url="avatarUrl"
                        />
                        <img
                            v-else
                            :src="avatarUrl"
                            alt=""
                        >
                    </div>
                    <!--/.comment__avatarImage-->
                </div>
                <!--/.comment__avatar-->
                <div class="comment__main">
                    <div class="comment__body">
                        <div class="comment__info">
                            <p class="comment__name" v-if="!isRemoved">
                                {{ comment.user_full_name }}
                            </p>
                            <time class="comment__posted">{{ publishedAt }}</time>
                            <div class="comment__actions">
                                <button
                                    v-if="isCurrentUsersComment"
                                    v-tooltip="'Edit'"
                                    class="comment__action stripButton"
                                    @click="isEditing = true"
                                >
                                    <i
                                        class="fas fa-pencil icon"
                                    />
                                </button>
                                <button
                                    v-if="isCurrentUsersComment"
                                    v-tooltip="'Remove'"
                                    class="comment__action stripButton"
                                    @click="openRemoveModal"
                                >
                                    <i
                                        class="fas fa-trash icon"
                                    />
                                </button>
                                <button
                                    v-if="showFlagButton"
                                    v-tooltip="'Report'"
                                    class="comment__action stripButton"
                                    @click="openFlagModal"
                                >
                                    <i
                                        class="fas fa-exclamation-triangle icon"
                                    />
                                </button>
                            </div>
                            <!--/.comment__actions-->
                        </div>
                        <!--/.comment__info-->
                        <div
                            class="comment__content"
                            v-html="comment.body" />
                    </div>
                    <!--/.comment__body-->
                    <button
                        v-if="!parentId"
                        class="comment__reply stripButton"
                        @click="openReply"
                    >
                        <i class="fas fa-plus icon" /><span>Reply</span>
                    </button>
                    <div class="comment__replyComposer">
                        <comment-composer
                            v-if="isReplying"
                            :parent-id="comment.id"
                            :placeholder="`Reply to ${user.fullName}`"
                            submit-text="Reply"
                            :show-cancel="true"
                            @commentAdded="isReplying = false"
                        />
                    </div>
                    <!--/.comment__replyComposer-->
                </div>
                <!--/.comment__main-->
            </template>
            <div class="comment__editComposer" v-if="isEditing">
                <comment-editor
                    :content="comment.body"
                    :comment-id="comment.id"
                    :parent-id="parentId"
                    @close="isEditing = false"
                />
            </div>
            <!--/.comment__editComposer-->
        </div>
        <!--/.comment__inner-->
        <transition-group
            v-if="comment.children"
            :name="initialSetDone ? 'comments-list' : 'none'"
            tag="ul"
            class="comments__list -children"
        >
            <li
                v-for="child in comment.children"
                :key="child.id"
                class="comments__listItem"
            >
                <comment
                    class="-child"
                    :comment="child"
                    :parent-id="comment.id"
                />
            </li>
        </transition-group>
    </div>
    <!--/.comment-->
</template>

<script>
import { mapState } from 'vuex';

import moment from 'moment';
import CommentComposer from './CommentComposer.vue';
import CommentEditor from './CommentEditor.vue';
import AvatarImage from '../AvatarImage.vue';

export default {
    name: 'Comment',
    components: {
        AvatarImage,
        CommentComposer,
        CommentEditor,
    },
    props: {
        comment: {
            type: Object,
            required: true,
        },
        parentId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            isReplying: false,
            isEditing: false,
        };
    },
    computed: {
        ...mapState(['user']),
        ...mapState('comments', ['initialSetDone']),
        publishedAt() {
            return moment(this.comment.created_at).format('MMMM Do YYYY');
        },
        isCurrentUsersComment() {
            return this.comment.user_id === this.user.id;
        },
        avatarUrl() {
            return this.comment.user_avatar ? this.comment.user_avatar : '/images/frontend/default-avatar.png';
        },
        isRemoved() {
            return this.comment.status === 'Deleted';
        },
        isCurrentUserComment() {
            return this.user.id === this.comment.user_id;
        },
        isFlaggedByCurrentUser() {
            return this.comment.is_flagged_by_user;
        },
        showFlagButton() {
            return !this.isCurrentUserComment && !this.isRemoved && !this.isFlaggedByCurrentUser;
        },
    },
    methods: {
        openReply() {
            this.isReplying = true;
        },
        openRemoveModal() {
            this.$modal.show('comment-remove', {
                commentId: this.comment.id,
                parentId: this.parentId,
            });
        },
        openFlagModal() {
            this.$modal.show('comment-flag', {
                commentId: this.comment.id,
            });
        },
    },
};
</script>

<style lang="scss">
    .comments-list-enter-active, .comments-list-leave-active,
    .comments-list-enter-active .comment__body {
        transition: all 0.6s 0.3s;
    }
    .comments-list-move {
        transition: transform 0.6s;
    }
    .comments-list-enter, .comments-list-leave-to {
        opacity: 0;
        transform: translateY(-1.5rem);
    }
    .comments-list-enter .comment__body {
        background-color: hsl(209, 53%, 50%);
    }
</style>
